import { AuthLayout } from '@/bundle/Auth/ui/AuthLayout/AuthLayout';
import { AuthStep } from '@/bundle/Auth/ui/AuthStep/AuthStep';
import { AuthTitle } from '@/bundle/Auth/ui/AuthTitle/AuthTitle';
import { Notification } from '@/components/Notification/Notification';
import { useFormik } from 'formik';
import { passwordValidationSchema } from '@/bundle/Auth/InviteUserFlow/SetInitialPasswordPage/const/passwordValidationSchema';
import { useMutation } from '@tanstack/react-query';
import { ACTIVATION_TOKEN_INVALID_RESPONSE } from '@/api/const';
import { getSetPasswordLinkExpiredPath } from '@/bundle/Auth/InviteUserFlow/SetPasswordLinkExpiredPage/path/path';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { useNavigate } from 'react-router';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { SetPasswordPayloadType } from '@/api/v1/users/setPasswordApi';
import { useAuthNavigateToStep } from '../../hooks/useAuthNavigateToStep';
import { SetPasswordForm } from '@/bundle/shared/components/SetPasswordForm/SetPasswordForm';
import { AdminOnboardingStepper } from '../ui/AdminOnboardingStepper';
import { useAdminOnboardingFlowFromAuthToken } from '@/bundle/shared/hooks/useAdminOnboardingFlowFromAuthToken';
import { AuthMessage } from '../../ui/styles';
import { Box } from '@/components/Box/Box';
import { setPassword } from '../SetPasswordPage/api/api';

type PasswordConfirmationFormType = { password: string; passwordConfirmation: string };

// Master Admin has the same "set password" page as rest users expect different URL and heading text.
export const AdminSetPasswordPage = () => {
  const navigate = useNavigate();
  const navigateToStep = useAuthNavigateToStep();
  const { isOnboardingFlow, signUpStep } = useAdminOnboardingFlowFromAuthToken();

  const { mutate, data, isPending } = useMutation({
    mutationKey: ['admin_set_password'],
    mutationFn: (values: SetPasswordPayloadType) => {
      return setPassword(values);
    },
    onSuccess(response) {
      const error = response?.error;

      if (error) {
        if (error?.errorContent === ACTIVATION_TOKEN_INVALID_RESPONSE) {
          navigate(getSetPasswordLinkExpiredPath());
        }

        return;
      }

      amplitudeService.logEvent(AMPLITUDE_EVENTS.SetPasswordSuccess);

      navigateToStep(response?.body);
    },
  });

  const formik = useFormik<PasswordConfirmationFormType>({
    initialValues: {
      password: '',
      passwordConfirmation: '',
    },
    validationSchema: passwordValidationSchema,
    onSubmit: (values) => {
      const payload = {
        password: values.password,
      };

      mutate(payload);
    },
  });

  const error = getResponseError(data?.error);

  return (
    <AuthLayout banner={isOnboardingFlow && <AdminOnboardingStepper signUpStep={signUpStep} />}>
      <AuthStep top={0} width='544px'>
        <AuthTitle>Set Your Password to Complete Setup</AuthTitle>
        <Box marginBottom='28px'>
          <AuthMessage>
            Secure passwords help protect your account, wire instructions, and transaction history.
          </AuthMessage>
        </Box>

        {error && (
          <Notification variant='error' marginBottom='24px'>
            {error}
          </Notification>
        )}

        <SetPasswordForm formik={formik} apiError={data?.error} isLoading={isPending} />
      </AuthStep>
    </AuthLayout>
  );
};
